// Shared validation for onboarding image uploads.
//
// We intentionally do NOT validate against a strict MIME whitelist. Mobile
// browsers frequently hand back a File with an empty `type` (common on Android
// file pickers) or a HEIC/HEIF type (iOS camera default). Rejecting those broke
// photo upload on mobile even though the backend accepts any content type and
// stores the file as-is. The `<input accept="image/*">` already restricts the
// picker; here we just accept anything the browser reports as an image (or an
// unknown type) and enforce the size cap.

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export type ImageValidationResult =
  | { ok: true }
  | { ok: false; reason: "type" | "size" };

export function validateImageFile(
  file: File,
  maxBytes: number = MAX_IMAGE_BYTES,
): ImageValidationResult {
  // Empty type => browser couldn't determine it (common on mobile); allow it.
  // Otherwise require an image/* type so obvious non-images are still caught.
  if (file.type && !file.type.startsWith("image/")) {
    return { ok: false, reason: "type" };
  }
  if (file.size > maxBytes) {
    return { ok: false, reason: "size" };
  }
  return { ok: true };
}

// iOS cameras produce HEIC/HEIF, which most non-Apple browsers can't render in
// an <img> tag. Detect those so we can transcode to JPEG before upload. The
// file picker sometimes reports an empty type on mobile, so fall back to the
// filename extension.
function isHeic(file: File): boolean {
  const type = file.type.toLowerCase();
  if (type === "image/heic" || type === "image/heif") return true;
  if (type === "") return /\.(heic|heif)$/i.test(file.name);
  return false;
}

// Returns a browser-renderable File ready for upload. HEIC/HEIF inputs are
// transcoded to JPEG; everything else is returned unchanged. heic2any is loaded
// lazily so its ~1.5 MB of libheif wasm only ships to clients that need it.
export async function prepareImageForUpload(file: File): Promise<File> {
  if (!isHeic(file)) return file;

  const { default: heic2any } = await import("heic2any");
  const converted = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: 0.9,
  });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  const baseName = file.name.replace(/\.(heic|heif)$/i, "") || "photo";
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
}
